import React, { useEffect, useMemo, useState } from 'react';
import {
  MdBarChart,
  MdEventAvailable,
  MdPeople,
  MdAccessTime,
  MdTrendingUp,
  MdSearch,
  MdClose,
  MdVisibility,
  MdEdit,
  MdAdd,
  MdAddCircleOutline,
  MdContentCopy,
  MdToggleOn,
  MdToggleOff,
  MdFileDownload,
  MdCalendarToday,
  MdLocationOn,
} from 'react-icons/md';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import { surveyApi } from '../../../api/surveyApi';
import { helpdeskLookupApi } from '../../../api/helpdeskApi';
import { locationApi } from '../../../api/adminapi';
import { getRoleFromToken } from '../../../utils/jwtUtils';
import ConfirmDialog from '../../../Components/ConfirmDialog';
import MultiSelectCheckboxDropdown from '../../../Components/MultiSelectCheckboxDropdown';
import './WorkspaceAnalytics.css';
import './Survey.css';

const QUESTION_TYPES = ['TEXT', 'RATING', 'SINGLE_CHOICE', 'MULTI_CHOICE', 'DROPDOWN'];
const OPTION_BASED_TYPES = new Set(['SINGLE_CHOICE', 'MULTI_CHOICE', 'DROPDOWN']);
const ROLE_OPTIONS = [
  'SUPER_ADMIN',
  'ADMIN',
  'USER',
  'GATEMEN',
  'REGISTERED_USER',
  'RECRUITER',
  'SR_RECRUITER',
  'HR_HEAD',
  'HIRING_MANAGER',
];
const TARGET_TYPE_OPTIONS = [
  { value: 'ROLE', label: 'Role' },
  { value: 'DEPARTMENT', label: 'Department' },
];

// The backend stores an empty location list to mean "not location-specific" (every location,
// including ones added later). Resolving that into "every currently active location selected"
// on load is what makes the Select All checkbox come up checked for those surveys.
const toSelectedLocations = (surveyLocations, allLocationNames) => {
  const raw = Array.isArray(surveyLocations)
    ? surveyLocations
    : (surveyLocations ? [surveyLocations] : []);
  const trimmed = raw.map((value) => String(value || '').trim()).filter(Boolean);
  return trimmed.length ? trimmed : [...allLocationNames];
};

// Selecting every currently active location is submitted as "all locations" (empty list) rather
// than a fixed list, so the survey also covers any location added after it's created.
const toLocationsPayload = (selectedLocations, allLocationNames) => (
  selectedLocations.length && selectedLocations.length === allLocationNames.length
    ? []
    : selectedLocations
);

const formatRoleLabel = (role) => role.replace(/_/g, ' ');

const getDuplicateDisplayOrders = (questionList) => {
  const counts = {};
  (questionList || []).forEach((question) => {
    const order = Number(question?.displayOrder);
    if (!Number.isFinite(order)) return;
    counts[order] = (counts[order] || 0) + 1;
  });

  return new Set(
    Object.keys(counts)
      .filter((key) => counts[key] > 1)
      .map(Number)
  );
};

const createBlankEditQuestion = () => ({
  question: '',
  type: 'TEXT',
  required: false,
  displayOrder: 1,
  optionsText: '',
});

const createSurveyPopupSnapshot = ({ title, validTillDateTime, locations, questions, targetType, targetValues }) => {
  const normalizedQuestions = (questions || []).map((question) => ({
    question: String(question?.question || '').trim(),
    type: question?.type || 'TEXT',
    required: Boolean(question?.required),
    displayOrder: Number(question?.displayOrder || 0),
    optionsText: String(question?.optionsText || '').trim(),
  }));

  return JSON.stringify({
    title: String(title || '').trim(),
    validTillDateTime: String(validTillDateTime || ''),
    locations: Array.isArray(locations) ? [...locations].sort() : [],
    targetType: String(targetType || ''),
    targetValues: Array.isArray(targetValues) ? [...targetValues].sort() : [],
    questions: normalizedQuestions,
  });
};

// The form keeps validTill as a 'YYYY-MM-DDTHH:mm' string (what the API helper expects),
// so the picker's Date is converted at the boundary rather than changing the stored shape.
const parseDateTimeLocal = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Greys out times already past - only bites on today, since future days are always ahead of now.
const isFutureTime = (time) => new Date(time).getTime() > Date.now();

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 16);
  }

  const pad = (v) => String(v).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatDateTimeForApi = (dateTimeValue) => {
  if (!dateTimeValue) return '';
  return dateTimeValue.length === 16 ? `${dateTimeValue}:00` : dateTimeValue;
};

const normalizeDashboard = (payload = {}) => {
  const totalSurveys = Number(payload.totalSurveys || 0);
  const totalResponses = Number(payload.totalResponses || 0);
  const pendingResponses = Number(payload.pendingResponses || 0);

  const rawCompletionRate = payload.completionRate ?? payload.completion_percentage ?? payload.completionPercentage;
  let completionRate = Number(rawCompletionRate);

  // Handle APIs that return ratio (0 to 1) instead of percentage.
  if (Number.isFinite(completionRate) && completionRate > 0 && completionRate <= 1) {
    completionRate = completionRate * 100;
  }

  // If rate is missing/invalid, derive from responses and pending counts.
  if (!Number.isFinite(completionRate)) {
    const eligibleUsers = totalResponses + pendingResponses;
    completionRate = eligibleUsers > 0 ? (totalResponses / eligibleUsers) * 100 : 0;
  }

  return {
    totalSurveys,
    totalResponses,
    pendingResponses,
    completionRate: Math.round(completionRate),
  };
};

const normalizeSurvey = (item = {}) => ({
  surveyId: String(item.surveyId || item.id || item.uuid || ''),
  surveyName: item.surveyName || item.title || item.surveyTitle || 'Untitled Survey',
  locations: Array.isArray(item.locations) ? item.locations : [],
  createdBy: item.createdBy || item.createdByName || '-',
  createdDate: item.createdDate || item.createdAt || '',
  validTill: item.validTill || item.validUntil || item.expiryDate || item.endDate || '',
  completed: Number(item.completed || 0),
  pending: Number(item.pending || 0),
  status: String(item.status || 'ACTIVE').toUpperCase(),
  expired: Boolean(item.expired),
});

const formatQuestionTypeLabel = (type) => {
  const normalized = String(type || '').trim();
  if (!normalized) return 'Unknown';
  return normalized
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const normalizePendingUser = (item = {}) => ({
  name: item.name || item.fullName || '-',
  email: item.email || '',
  empId: item.empId || '',
  department: item.department || '',
  location: item.location || '',
});

const normalizeResponse = (item = {}) => ({
  userId: String(item.userId || item.id || item.uuid || ''),
  userEmail: item.userEmail || item.email || '-',
  submittedAt: item.submittedAt || item.submittedOn || item.createdAt || '',
  answers: Array.isArray(item.answers)
    ? item.answers.map((ans) => ({
      question: ans?.question || ans?.questionText || 'Question',
      questionType: ans?.questionType || ans?.type || '',
      answer: ans?.answer || ans?.value || '-',
    }))
    : [],
});

/**
 * Answers carry no question id, so they are keyed by question text plus how many times that
 * text has already appeared in the same response - this keeps surveys that repeat a question
 * text from collapsing into one column.
 */
const indexAnswersByKey = (answers) => {
  const occurrences = new Map();
  const answerByKey = new Map();

  answers.forEach((answer) => {
    const occurrence = (occurrences.get(answer.question) || 0) + 1;
    occurrences.set(answer.question, occurrence);
    answerByKey.set(`${answer.question}#${occurrence}`, answer);
  });

  return answerByKey;
};

/**
 * Canonical question order for the export, in first-seen order across all responses, so that
 * "Question N" refers to the same question on every row even when a respondent skipped one.
 */
const buildQuestionColumns = (responses) => {
  const columns = [];
  const seen = new Set();

  responses.forEach((response) => {
    const occurrences = new Map();

    response.answers.forEach((answer) => {
      const occurrence = (occurrences.get(answer.question) || 0) + 1;
      occurrences.set(answer.question, occurrence);

      const key = `${answer.question}#${occurrence}`;
      if (!seen.has(key)) {
        seen.add(key);
        columns.push({ key, question: answer.question, questionType: answer.questionType });
      }
    });
  });

  return columns;
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Matches the header styling already used for the ID card report export in HelpdeskReports.js,
// so downloaded reports read as one consistent brand.
const styleReportHeaderRow = (row) => {
  row.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF704493' } };
  row.alignment = { vertical: 'middle', horizontal: 'center' };
  row.height = 22;
};

function SurveyAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [role, setRole] = useState('');

  const [dashboard, setDashboard] = useState(normalizeDashboard());
  const [surveys, setSurveys] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesError, setResponsesError] = useState('');
  const [responses, setResponses] = useState([]);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [downloadingSurveyId, setDownloadingSurveyId] = useState('');
  const [pendingUsersLoading, setPendingUsersLoading] = useState(false);
  const [pendingUsersError, setPendingUsersError] = useState('');
  const [pendingUsers, setPendingUsers] = useState([]);
  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
  const [pendingUsersLoaded, setPendingUsersLoaded] = useState(false);
  const [isResponsesModalOpen, setIsResponsesModalOpen] = useState(false);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [surveyFormMode, setSurveyFormMode] = useState('edit');
  const [editSurveyId, setEditSurveyId] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editValidTillDateTime, setEditValidTillDateTime] = useState('');
  const [isValidTillOpen, setIsValidTillOpen] = useState(false);
  const [editSelectedLocations, setEditSelectedLocations] = useState([]);
  const [editQuestions, setEditQuestions] = useState([createBlankEditQuestion()]);
  const [cloneTargetType, setCloneTargetType] = useState('ROLE');
  const [cloneTargetValues, setCloneTargetValues] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [initialPopupSnapshot, setInitialPopupSnapshot] = useState('');
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const depts = await helpdeskLookupApi.getDepartments();
        setDepartments(Array.isArray(depts) ? depts : []);
      } catch (e) { console.error(e); }
    };
    loadDepartments();
  }, []);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await locationApi.getAllActiveLocationNames();
        const locationNames = response?.data || response || [];
        setLocations(Array.isArray(locationNames) ? locationNames : []);
      } catch (e) {
        console.error(e);
        setLocations([]);
      }
    };
    loadLocations();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (!token) return;

    try {
      const claims = getRoleFromToken(token);
      setRole(String(claims?.role || '').toUpperCase());
    } catch (err) {
      setRole('');
    }
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');

    try {
      const [dashboardPayload, surveysPayload] = await Promise.all([
        surveyApi.getAnalyticsDashboard(),
        surveyApi.getAnalyticsSurveys(),
      ]);

      const surveyList = Array.isArray(surveysPayload)
        ? surveysPayload
        : Array.isArray(surveysPayload?.surveys)
          ? surveysPayload.surveys
          : [];

      setDashboard(normalizeDashboard(dashboardPayload));
      setSurveys(surveyList.map(normalizeSurvey).filter((item) => item.surveyId));
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load survey analytics.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const filteredSurveys = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const fromDate = dateFromFilter ? new Date(dateFromFilter) : null;
    const toDate = dateToFilter ? new Date(`${dateToFilter}T23:59:59.999`) : null;

    return surveys.filter((item) => {
      if (term && !item.surveyName.toLowerCase().includes(term)) return false;
      if (locationFilter && item.locations.length && !item.locations.includes(locationFilter)) return false;

      if (fromDate || toDate) {
        const createdDate = item.createdDate ? new Date(item.createdDate) : null;
        if (!createdDate || Number.isNaN(createdDate.getTime())) return false;
        if (fromDate && createdDate < fromDate) return false;
        if (toDate && createdDate > toDate) return false;
      }

      return true;
    });
  }, [searchTerm, locationFilter, dateFromFilter, dateToFilter, surveys]);

  const handleDateFromFilterChange = (value) => {
    if (value && dateToFilter && value >= dateToFilter) {
      toast.warning('Start date must be before the end date.');
      return;
    }
    setDateFromFilter(value);
    setCurrentPage(1);
  };

  const handleDateToFilterChange = (value) => {
    if (value && dateFromFilter && value <= dateFromFilter) {
      toast.warning('End date must be after the start date.');
      return;
    }
    setDateToFilter(value);
    setCurrentPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filteredSurveys.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const visibleSurveys = filteredSurveys.slice(pageStart, pageStart + pageSize);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const loadDrawerResponses = async (surveyId) => {
    setResponses([]);
    setResponsesError('');
    setResponsesLoading(true);

    try {
      const payload = await surveyApi.getAnalyticsResponses(surveyId);
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.responses)
          ? payload.responses
          : [];
      setResponses(list.map(normalizeResponse));
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load submitted responses.';
      setResponsesError(message);
      toast.error(message);
    } finally {
      setResponsesLoading(false);
    }
  };

  const loadDrawerPendingUsers = async (surveyId) => {
    setPendingUsersError('');
    setPendingUsersLoading(true);

    try {
      const payload = await surveyApi.getAnalyticsPendingUsers(surveyId);
      const list = Array.isArray(payload) ? payload : Array.isArray(payload?.pendingUsers) ? payload.pendingUsers : [];
      setPendingUsers(list.map(normalizePendingUser));
      setPendingUsersLoaded(true);
    } catch (err) {
      // Shown inline in the pending section rather than toasted - the panel is opt-in, so an
      // error here shouldn't interrupt whoever is reading the responses table.
      setPendingUsersError(err?.response?.data?.message || err?.message || 'Failed to load pending users.');
    } finally {
      setPendingUsersLoading(false);
    }
  };

  // The list is fetched when the popup is first opened, not on drawer open - the count already
  // comes from the analytics row, so there is no reason to pull the whole list until asked.
  const openPendingUsersModal = () => {
    setIsPendingModalOpen(true);

    if (!pendingUsersLoaded && !pendingUsersLoading && selectedSurvey) {
      loadDrawerPendingUsers(selectedSurvey.surveyId);
    }
  };

  const resetPendingUsers = () => {
    setPendingUsers([]);
    setPendingUsersError('');
    setPendingUsersLoaded(false);
    setIsPendingModalOpen(false);
  };

  const openSurveyDrawer = async (survey) => {
    setSelectedSurvey(survey);
    resetPendingUsers();
    await loadDrawerResponses(survey.surveyId);
  };

  const closeSurveyDrawer = () => {
    setSelectedSurvey(null);
    setResponses([]);
    setResponsesError('');
    setIsResponsesModalOpen(false);
    resetPendingUsers();
  };

  const handleDownloadReport = async (survey) => {
    setDownloadingSurveyId(survey.surveyId);
    try {
      // Pending users is best-effort - if it fails, still deliver the completed responses
      // rather than failing the whole download over a secondary list.
      const [responsesResult, pendingResult] = await Promise.allSettled([
        surveyApi.getAnalyticsResponses(survey.surveyId),
        surveyApi.getAnalyticsPendingUsers(survey.surveyId),
      ]);

      if (responsesResult.status === 'rejected') {
        throw responsesResult.reason;
      }

      const responsePayload = responsesResult.value;
      const responseList = Array.isArray(responsePayload)
        ? responsePayload
        : Array.isArray(responsePayload?.responses)
          ? responsePayload.responses
          : [];
      const normalizedResponses = responseList.map(normalizeResponse);

      const pendingPayload = pendingResult.status === 'fulfilled' ? pendingResult.value : [];
      const pendingList = Array.isArray(pendingPayload)
        ? pendingPayload
        : Array.isArray(pendingPayload?.pendingUsers)
          ? pendingPayload.pendingUsers
          : [];
      const normalizedPending = pendingList.map(normalizePendingUser);

      if (!normalizedResponses.length && !normalizedPending.length) {
        toast.info('No data to download for this survey.');
        return;
      }

      const ExcelJS = await import('exceljs').then((m) => m.default);
      const workbook = new ExcelJS.Workbook();

      if (normalizedResponses.length) {
        // One row per respondent, with each answer pivoted into its own
        // "Question N / Question Type N / Answer N" column group.
        const questionColumns = buildQuestionColumns(normalizedResponses);

        const headers = ['User Email', 'Submitted On'];
        questionColumns.forEach((_, index) => {
          const position = index + 1;
          headers.push(`Question ${position}`, `Question Type ${position}`, `Answer ${position}`);
        });

        const responsesSheet = workbook.addWorksheet('Completed Responses');
        responsesSheet.addRow(headers);
        styleReportHeaderRow(responsesSheet.getRow(1));

        normalizedResponses.forEach((response) => {
          const answerByKey = indexAnswersByKey(response.answers);
          const row = [response.userEmail, formatDateTime(response.submittedAt)];

          questionColumns.forEach((column) => {
            const answer = answerByKey.get(column.key);
            row.push(
              column.question,
              formatQuestionTypeLabel(column.questionType),
              answer ? String(answer.answer || '-') : '-'
            );
          });

          responsesSheet.addRow(row);
        });

        responsesSheet.columns.forEach((col) => { col.width = 22; });
      }

      if (normalizedPending.length) {
        const pendingSheet = workbook.addWorksheet('Pending Action');
        pendingSheet.addRow(['Name', 'Email', 'Employee ID', 'Department', 'Location']);
        styleReportHeaderRow(pendingSheet.getRow(1));

        normalizedPending.forEach((user) => {
          pendingSheet.addRow([user.name, user.email, user.empId, user.department, user.location]);
        });

        pendingSheet.columns.forEach((col) => { col.width = 22; });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const safeName = (survey.surveyName || 'Survey').replace(/[^a-z0-9]+/gi, '_').slice(0, 50);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeName}_Report.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to download survey report.');
    } finally {
      setDownloadingSurveyId('');
    }
  };

  const currentPopupSnapshot = createSurveyPopupSnapshot({
    title: editTitle,
    validTillDateTime: editValidTillDateTime,
    locations: editSelectedLocations,
    questions: editQuestions,
    targetType: cloneTargetType,
    targetValues: cloneTargetValues,
  });
  const hasUnsavedPopupChanges = initialPopupSnapshot && currentPopupSnapshot !== initialPopupSnapshot;

  const performCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSurveyFormMode('edit');
    setEditSurveyId('');
    setEditTitle('');
    setEditValidTillDateTime('');
    setIsValidTillOpen(false);
    setEditSelectedLocations([]);
    setEditQuestions([createBlankEditQuestion()]);
    setCloneTargetType('ROLE');
    setCloneTargetValues([]);
    setInitialPopupSnapshot('');
    setShowCloseConfirm(false);
  };

  const closeEditModal = (forceClose = false) => {
    if (!forceClose && hasUnsavedPopupChanges) {
      setShowCloseConfirm(true);
      return;
    }

    performCloseEditModal();
  };

  const isSurveyAdmin = role === 'SUPER_ADMIN' || role === 'ADMIN';

  const handleEditQuestionChange = (index, key, value) => {
    setEditQuestions((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const handleAddEditQuestion = () => {
    setEditQuestions((prev) => [...prev, { ...createBlankEditQuestion(), displayOrder: prev.length + 1 }]);
  };

  const handleCloneTargetTypeChange = (nextType) => {
    setCloneTargetType(nextType);
    setCloneTargetValues([]);
  };

  const handleCloneTargetValueToggle = (value) => {
    setCloneTargetValues((prev) => (
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    ));
  };

  const handleRemoveEditQuestion = (index) => {
    setEditQuestions((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const validateEditPayload = () => {
    if (!editTitle.trim()) {
      toast.warning('Survey title is required.');
      return false;
    }

    if (!editValidTillDateTime) {
      toast.warning('Valid till date and time is required.');
      return false;
    }

    const validTillValue = new Date(editValidTillDateTime);
    if (Number.isNaN(validTillValue.getTime())) {
      toast.warning('Please select a valid date and time.');
      return false;
    }

    if (validTillValue <= new Date()) {
      toast.warning('Valid till must be a future date and time.');
      return false;
    }

    if (!editSelectedLocations.length) {
      toast.warning('Please select at least one location.');
      return false;
    }

    if (!editQuestions.length) {
      toast.warning('Add at least one question.');
      return false;
    }

    if (!cloneTargetValues.length) {
      toast.warning(`Please select at least one ${cloneTargetType === 'DEPARTMENT' ? 'department' : 'role'}.`);
      return false;
    }

    const hasInvalidQuestion = editQuestions.some((question) => !String(question.question || '').trim());
    if (hasInvalidQuestion) {
      toast.warning('Each question must have text.');
      return false;
    }

    for (const question of editQuestions) {
      const order = Number(question.displayOrder);
      if (!Number.isInteger(order) || order <= 0) {
        toast.warning('Display order must be a positive whole number for all questions.');
        return false;
      }
    }

    if (getDuplicateDisplayOrders(editQuestions).size > 0) {
      toast.warning('Display Order already exists. Please select a unique display order.');
      return false;
    }

    for (const question of editQuestions) {
      if (!OPTION_BASED_TYPES.has(question.type)) continue;
      const options = String(question.optionsText || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      if (options.length < 2) {
        toast.warning('Choice and dropdown questions require at least 2 options.');
        return false;
      }
    }

    return true;
  };

  const handleSaveSurveyForm = async () => {
    if (!validateEditPayload()) return;

    const basePayload = {
      title: editTitle.trim(),
      validTill: formatDateTimeForApi(editValidTillDateTime),
      locations: toLocationsPayload(editSelectedLocations, locations),
      targetType: cloneTargetType,
      targetValues: cloneTargetValues,
      questions: editQuestions
        .map((question) => {
          const options = String(question.optionsText || '')
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);

          return {
            question: question.question.trim(),
            type: question.type,
            required: Boolean(question.required),
            displayOrder: Number(question.displayOrder),
            options: OPTION_BASED_TYPES.has(question.type)
              ? options.map((option) => ({ label: option, value: option }))
              : [],
          };
        })
        .sort((a, b) => a.displayOrder - b.displayOrder),
    };

    setIsSavingEdit(true);
    try {
      if (surveyFormMode === 'clone') {
        await surveyApi.createSurvey(basePayload);
        toast.success('Survey cloned successfully.');
      } else {
        const updated = await surveyApi.updateSurvey(editSurveyId, basePayload);
        if (updated?.questionsLocked) {
          // Only a partial update landed - surface it in red so the discarded question
          // edits aren't mistaken for saved ones.
          toast.error(
            'Survey updated, but questions were not modified because this survey already has responses.',
            { autoClose: 8000 }
          );
        } else {
          toast.success('Survey updated successfully.');
        }
      }
      closeEditModal(true);
      loadAnalytics();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || `Failed to ${surveyFormMode === 'clone' ? 'clone' : 'update'} survey.`);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const openSurveyFormModal = async (survey, mode) => {
    setIsEditModalOpen(true);
    setSurveyFormMode(mode);
    setEditLoading(true);
    setEditSurveyId(survey.surveyId);
    setInitialPopupSnapshot('');

    try {
      const payload = await surveyApi.getSurveyById(survey.surveyId);
      const data = payload?.data || payload;
      const fetchedTitle = String(data?.title || survey.surveyName || '').trim();
      const resolvedTitle = mode === 'clone' ? `${fetchedTitle} (Copy)` : fetchedTitle;
      const resolvedValidTill = toDateTimeLocalValue(data?.validTill || data?.validUntil || data?.expiryDate || data?.endDate || '');
      const resolvedLocations = toSelectedLocations(data?.locations ?? data?.location, locations);
      const resolvedTargetType = data?.targetType || 'ROLE';
      const resolvedTargetValues = Array.isArray(data?.targetValues) ? data.targetValues : [];

      setEditTitle(resolvedTitle);
      setEditValidTillDateTime(resolvedValidTill);
      setEditSelectedLocations(resolvedLocations);
      setCloneTargetType(resolvedTargetType);
      setCloneTargetValues(resolvedTargetValues);

      const fetchedQuestions = Array.isArray(data?.questions) ? data.questions : [];
      if (!fetchedQuestions.length) {
        const defaultQuestions = [createBlankEditQuestion()];
        setEditQuestions(defaultQuestions);
        setInitialPopupSnapshot(
          createSurveyPopupSnapshot({
            title: resolvedTitle,
            validTillDateTime: resolvedValidTill,
            locations: resolvedLocations,
            questions: defaultQuestions,
            targetType: resolvedTargetType,
            targetValues: resolvedTargetValues,
          })
        );
      } else {
        const normalizedQuestions =
          fetchedQuestions
            .slice()
            .sort((a, b) => Number(a?.displayOrder || 0) - Number(b?.displayOrder || 0))
            .map((q, index) => {
              const order = Number(q?.displayOrder);
              return {
                question: String(q?.question || ''),
                type: q?.type || 'TEXT',
                required: Boolean(q?.required),
                displayOrder: Number.isFinite(order) && order > 0 ? order : index + 1,
                optionsText: Array.isArray(q?.options)
                  ? q.options.map((opt) => String(opt?.label || opt?.value || '').trim()).filter(Boolean).join('\n')
                  : '',
              };
            });

        setEditQuestions(
          normalizedQuestions
        );
        setInitialPopupSnapshot(
          createSurveyPopupSnapshot({
            title: resolvedTitle,
            validTillDateTime: resolvedValidTill,
            locations: resolvedLocations,
            questions: normalizedQuestions,
            targetType: resolvedTargetType,
            targetValues: resolvedTargetValues,
          })
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to load survey details.');
      closeEditModal();
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditSurvey = async (survey) => {
    await openSurveyFormModal(survey, 'edit');
  };

  const handleCloneSurvey = async (survey) => {
    await openSurveyFormModal(survey, 'clone');
  };

  const handleToggleStatus = async (survey) => {
    const nextStatus = survey.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await surveyApi.updateSurveyStatus(survey.surveyId, nextStatus);
      toast.success(`Survey marked as ${nextStatus === 'ACTIVE' ? 'active' : 'inactive'}.`);
      loadAnalytics();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update survey status.');
    }
  };

  const completionPercent = selectedSurvey
    ? Math.round((selectedSurvey.completed / Math.max(1, selectedSurvey.completed + selectedSurvey.pending)) * 100)
    : 0;

  if (loading) {
    return (
      <div className="workspace-analytics survey-analytics-ui">
        <div className="loading">Loading survey analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="workspace-analytics survey-analytics-ui">
        <div className="error-message">
          <p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={loadAnalytics}>Retry</button>
        </div>
      </div>
    );
  }

  const editDuplicateDisplayOrders = getDuplicateDisplayOrders(editQuestions);

  return (
    <div className="workspace-analytics survey-analytics-ui">
      <div className="page-header">
        <div className="header-left">
          <span className="survey-analytics-header-badge">
            <MdBarChart className="header-icon" />
          </span>
          <div>
            <h2>Survey Analytics</h2>
            <p className="survey-analytics-header-subtitle">
              Responses, completion rate and pending users across every survey
            </p>
          </div>
        </div>
      </div>

      <div className="stat-cards">
        <div className="stat-card stat-total">
          <div className="stat-icon"><MdEventAvailable /></div>
          <div className="stat-info">
            <span className="stat-value">{dashboard.totalSurveys}</span>
            <span className="stat-label">Total Surveys</span>
          </div>
        </div>

        <div className="stat-card stat-approved">
          <div className="stat-icon"><MdPeople /></div>
          <div className="stat-info">
            <span className="stat-value">{dashboard.totalResponses}</span>
            <span className="stat-label">Total Responses</span>
          </div>
        </div>

        <div className="stat-card stat-pending">
          <div className="stat-icon"><MdAccessTime /></div>
          <div className="stat-info">
            <span className="stat-value">{dashboard.pendingResponses}</span>
            <span className="stat-label">Pending Responses (Active &amp; Not Expired Surveys Only)</span>
          </div>
        </div>

        {/*
        <div className="stat-card stat-approval">
          <div className="stat-icon"><MdTrendingUp /></div>
          <div className="stat-info">
            <span className="stat-value">{dashboard.completionRate}%</span>
            <span className="stat-label">Completion Rate</span>
          </div>
        </div>
        */}
      </div>

      <div className="chart-card survey-analytics-card-shell">
        <div className="survey-analytics-table-head">
          <div className="input search-compact survey-search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              type="text"
              placeholder="Search by Survey Name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="survey-analytics-filters">
            <div className="survey-analytics-filter-item">
              <label htmlFor="survey-analytics-date-from">Start Date</label>
              <input
                id="survey-analytics-date-from"
                type="date"
                className="date-input-compact"
                value={dateFromFilter}
                max={dateToFilter || undefined}
                onChange={(e) => handleDateFromFilterChange(e.target.value)}
              />
            </div>
            <div className="survey-analytics-filter-item">
              <label htmlFor="survey-analytics-date-to">End Date</label>
              <input
                id="survey-analytics-date-to"
                type="date"
                className="date-input-compact"
                value={dateToFilter}
                min={dateFromFilter || undefined}
                onChange={(e) => handleDateToFilterChange(e.target.value)}
              />
            </div>
            <select
              className="filter-select-compact"
              value={locationFilter}
              onChange={(e) => {
                setLocationFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Locations</option>
              {locations.map((locationName) => (
                <option key={locationName} value={locationName}>{locationName}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="table-container">
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Survey Name</th>
                <th>Location</th>
                <th>Created By</th>
                <th>Created Date</th>
                <th>Valid Till (IST)</th>
                <th>Completed</th>
                <th>Pending</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleSurveys.length ? (
                visibleSurveys.map((survey) => (
                  <tr key={survey.surveyId}>
                    <td>{survey.surveyName}</td>
                    {/* An empty locations list is the "not location-specific" sentinel from the backend. */}
                    <td>{survey.locations.length ? survey.locations.join(', ') : 'All Locations'}</td>
                    <td>{survey.createdBy}</td>
                    <td>{formatDate(survey.createdDate)}</td>
                    <td>
                      {formatDateTime(survey.validTill)}
                      <br />
                      <span className={`survey-status-badge ${survey.expired ? 'expired' : 'not-expired'}`}>
                        {survey.expired ? 'EXPIRED' : 'NOT EXPIRED'}
                      </span>
                    </td>
                    <td>{survey.completed}</td>
                    <td>{survey.pending}</td>
                    <td>
                      <span className={`survey-status-badge ${survey.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                        {survey.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons" style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-small"
                          onClick={() => openSurveyDrawer(survey)}
                          title="View Survey Analytics"
                          aria-label="View Survey Analytics"
                        >
                          <MdVisibility />
                        </button>
                        {isSurveyAdmin && (
                          <button
                            type="button"
                            className="btn btn-small btn-secondary"
                            onClick={() => handleEditSurvey(survey)}
                            title="Edit Survey"
                            aria-label="Edit Survey"
                          >
                            <MdEdit />
                          </button>
                        )}
                        {isSurveyAdmin && (
                          <button
                            type="button"
                            className="btn btn-small btn-secondary"
                            onClick={() => handleCloneSurvey(survey)}
                            title="Clone Survey"
                            aria-label="Clone Survey"
                          >
                            <MdContentCopy />
                          </button>
                        )}
                        {isSurveyAdmin && (
                          <button
                            type="button"
                            className="btn btn-small btn-secondary"
                            onClick={() => handleToggleStatus(survey)}
                            title={survey.status === 'ACTIVE' ? 'Deactivate Survey' : 'Activate Survey'}
                            aria-label={survey.status === 'ACTIVE' ? 'Deactivate Survey' : 'Activate Survey'}
                          >
                            {survey.status === 'ACTIVE' ? <MdToggleOn /> : <MdToggleOff />}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-small btn-secondary"
                          onClick={() => handleDownloadReport(survey)}
                          disabled={downloadingSurveyId === survey.surveyId}
                          title="Download Report"
                          aria-label="Download Report"
                        >
                          <MdFileDownload />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="survey-analytics-state-cell">No surveys found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="survey-analytics-pagination">
          <span>
            Showing {filteredSurveys.length ? pageStart + 1 : 0}-{Math.min(pageStart + pageSize, filteredSurveys.length)} of {filteredSurveys.length}
          </span>
          <div className="survey-analytics-pagination-actions">
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedSurvey && (
        <>
          <div className="survey-analytics-drawer-overlay" onClick={closeSurveyDrawer} />
          <aside className="survey-analytics-drawer" role="dialog" aria-modal="true" aria-label="Survey Analytics Drawer">
            <div className="survey-analytics-drawer-head">
              <div className="survey-head-left">
                <span className="survey-head-badge">
                  <MdBarChart aria-hidden="true" />
                </span>
                <h3>Survey Analytics</h3>
              </div>
              <button type="button" className="survey-analytics-icon-btn" onClick={closeSurveyDrawer}>
                <MdClose />
              </button>
            </div>

            <div className="survey-drawer-summary">
              <p className="survey-drawer-survey-name">{selectedSurvey.surveyName}</p>
              <div className="survey-drawer-badges">
                <span className={`survey-status-badge ${selectedSurvey.expired ? 'expired' : 'not-expired'}`}>
                  {selectedSurvey.expired ? 'EXPIRED' : 'NOT EXPIRED'}
                </span>
                <span className="survey-drawer-location">
                  <MdLocationOn aria-hidden="true" />
                  {selectedSurvey.locations.length ? selectedSurvey.locations.join(', ') : 'All Locations'}
                </span>
              </div>

              <div className="survey-drawer-stats">
                <div className="survey-drawer-stat is-completed">
                  <span className="survey-drawer-stat-value">{selectedSurvey.completed}</span>
                  <span className="survey-drawer-stat-label">Completed</span>
                </div>
                <div className="survey-drawer-stat is-pending">
                  <span className="survey-drawer-stat-value">{selectedSurvey.pending}</span>
                  <span className="survey-drawer-stat-label">Pending</span>
                </div>
                <div className="survey-drawer-stat is-rate">
                  <span className="survey-drawer-stat-value">{completionPercent}%</span>
                  <span className="survey-drawer-stat-label">Completion</span>
                </div>
              </div>

              <div className="survey-analytics-progress">
                <div className="survey-analytics-progress-bar" style={{ width: `${completionPercent}%` }} />
              </div>
            </div>

            {/* Both breakdowns share one card shape so the drawer stays symmetric - the
                accent colour is the only thing that differs, matching the stat tiles above. */}
            <div className="survey-drawer-sections">
              <div className="survey-drawer-action-card is-submitted">
                <span className="survey-drawer-action-dot" aria-hidden="true" />
                <div className="survey-drawer-action-text">
                  <span className="survey-drawer-action-title">Submitted Responses</span>
                  <span className="survey-drawer-action-hint">
                    {responsesLoading
                      ? 'Loading responses...'
                      : responsesError
                        ? responsesError
                        : responses.length
                          ? `${responses.length} ${responses.length === 1 ? 'user has' : 'users have'} responded`
                          : 'No responses submitted yet'}
                  </span>
                </div>
                <div className="survey-drawer-action-side">
                  <span className="survey-drawer-count-pill is-submitted">{selectedSurvey.completed}</span>
                  <button
                    type="button"
                    className="btn btn-small"
                    onClick={() => setIsResponsesModalOpen(true)}
                    disabled={responsesLoading || !responses.length}
                    title={responses.length ? 'View Submitted Responses' : 'No submitted responses'}
                    aria-label="View Submitted Responses"
                  >
                    <MdVisibility />
                  </button>
                </div>
              </div>

              <div className="survey-drawer-action-card is-pending">
                <span className="survey-drawer-action-dot" aria-hidden="true" />
                <div className="survey-drawer-action-text">
                  <span className="survey-drawer-action-title">Pending Responses</span>
                  <span className="survey-drawer-action-hint">
                    {selectedSurvey.pending
                      ? `${selectedSurvey.pending} targeted ${selectedSurvey.pending === 1 ? 'user has' : 'users have'} not responded`
                      : 'Everyone targeted has responded'}
                  </span>
                </div>
                <div className="survey-drawer-action-side">
                  <span className="survey-drawer-count-pill is-pending">{selectedSurvey.pending}</span>
                  <button
                    type="button"
                    className="btn btn-small"
                    onClick={openPendingUsersModal}
                    disabled={!selectedSurvey.pending}
                    title={selectedSurvey.pending ? 'View Pending Responses' : 'No pending responses'}
                    aria-label="View Pending Responses"
                  >
                    <MdVisibility />
                  </button>
                </div>
              </div>
            </div>
          </aside>
        </>
      )}

      {/* Rendered before the answers modal so that, at equal z-index, opening a submission's
          answers from this list stacks on top rather than behind. */}
      {isResponsesModalOpen && selectedSurvey && (
        <div className="survey-answers-modal-overlay" onClick={() => setIsResponsesModalOpen(false)}>
          <div
            className="survey-answers-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Submitted Responses"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="survey-answers-modal-head">
              <h3>Submitted Responses</h3>
              <button type="button" className="survey-analytics-icon-btn" onClick={() => setIsResponsesModalOpen(false)}>
                <MdClose />
              </button>
            </div>

            <p className="survey-answers-modal-subtitle">
              {selectedSurvey.surveyName}
              {!responsesLoading && !responsesError ? ` • ${responses.length} submitted` : ''}
            </p>

            <div className="table-container survey-drawer-table">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>User Email</th>
                    <th>Submitted On</th>
                    <th>View Answers</th>
                  </tr>
                </thead>
                <tbody>
                  {responsesLoading ? (
                    <tr>
                      <td colSpan="3" className="survey-analytics-state-cell">Loading submitted responses...</td>
                    </tr>
                  ) : responsesError ? (
                    <tr>
                      <td colSpan="3" className="survey-analytics-state-cell error">{responsesError}</td>
                    </tr>
                  ) : responses.length ? (
                    responses.map((response, idx) => (
                      <tr key={response.userId || `${response.userEmail}-${idx}`}>
                        <td>{response.userEmail}</td>
                        <td>{formatDateTime(response.submittedAt)}</td>
                        <td>
                          <div className="action-buttons" style={{ display: 'flex', justifyContent: 'center' }}>
                            <button
                              type="button"
                              className="btn btn-small"
                              onClick={() => setSelectedResponse(response)}
                              title="View Answers"
                              aria-label="View Answers"
                            >
                              <MdVisibility />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="survey-analytics-state-cell">No submitted responses found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {selectedResponse && (
        <div className="survey-answers-modal-overlay" onClick={() => setSelectedResponse(null)}>
          <div className="survey-answers-modal" role="dialog" aria-modal="true" aria-label="Submitted Answers" onClick={(e) => e.stopPropagation()}>
            <div className="survey-answers-modal-head">
              <h3>Submitted Answers</h3>
              <button type="button" className="survey-analytics-icon-btn" onClick={() => setSelectedResponse(null)}>
                <MdClose />
              </button>
            </div>

            <p className="survey-answers-modal-subtitle">{selectedResponse.userEmail}</p>

            <div className="survey-answers-list">
              {(selectedResponse.answers || []).length ? (
                selectedResponse.answers.map((item, idx) => (
                  <div className="survey-answer-item" key={`${item.question}-${idx}`}>
                    <p className="survey-answer-label">Question:</p>
                    <p className="survey-answer-value">{item.question}</p>
                    <p className="survey-answer-label">Question Type:</p>
                    <p className="survey-answer-value">{formatQuestionTypeLabel(item.questionType)}</p>
                    <p className="survey-answer-label">Answer:</p>
                    <p className="survey-answer-value">{String(item.answer || '-')}</p>
                  </div>
                ))
              ) : (
                <p className="survey-analytics-empty">No answers available for this submission.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {isPendingModalOpen && selectedSurvey && (
        <div className="survey-answers-modal-overlay" onClick={() => setIsPendingModalOpen(false)}>
          <div
            className="survey-answers-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Pending Responses"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="survey-answers-modal-head">
              <h3>Pending Responses</h3>
              <button type="button" className="survey-analytics-icon-btn" onClick={() => setIsPendingModalOpen(false)}>
                <MdClose />
              </button>
            </div>

            <p className="survey-answers-modal-subtitle">
              {selectedSurvey.surveyName}
              {!pendingUsersLoading && !pendingUsersError ? ` • ${pendingUsers.length} yet to respond` : ''}
            </p>

            <div className="survey-pending-users">
              {pendingUsersLoading ? (
                <p className="survey-pending-users-state">Loading pending responses...</p>
              ) : pendingUsersError ? (
                <p className="survey-pending-users-state error">{pendingUsersError}</p>
              ) : pendingUsers.length ? (
                <ul className="survey-pending-users-list">
                  {pendingUsers.map((user, idx) => (
                    <li key={user.email || `${user.name}-${idx}`} className="survey-pending-user">
                      <span className="survey-pending-user-name">{user.name}</span>
                      <span className="survey-pending-user-meta">
                        {[user.email, user.empId, user.department, user.location].filter(Boolean).join(' • ')}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="survey-pending-users-state">No pending responses for this survey.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div className="survey-answers-modal-overlay" onClick={() => closeEditModal()}>
          <div className="survey-edit-modal" role="dialog" aria-modal="true" aria-label="Edit Survey" onClick={(e) => e.stopPropagation()}>
            <div className="survey-answers-modal-head survey-edit-modal-head">
              <div className="survey-head-left">
                <span className="survey-head-badge">
                  {surveyFormMode === 'clone' ? <MdContentCopy aria-hidden="true" /> : <MdEdit aria-hidden="true" />}
                </span>
                <div>
                  <h3>{surveyFormMode === 'clone' ? 'Clone Survey' : 'Edit Survey'}</h3>
                  <p className="survey-modal-head-subtitle">
                    {surveyFormMode === 'clone'
                      ? 'Saves as a new survey - questions and targeting are copied across'
                      : 'Questions cannot be changed once a survey has responses'}
                  </p>
                </div>
              </div>
              <button type="button" className="survey-analytics-icon-btn" onClick={() => closeEditModal()}>
                <MdClose />
              </button>
            </div>

            {editLoading ? (
              <div className="survey-analytics-state-cell">Loading survey details...</div>
            ) : (
              <>
                <div className="survey-form-group">
                  <label htmlFor="edit-survey-title">Survey Title <span className="required">*</span></label>
                  <input
                    id="edit-survey-title"
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Feedback survey"
                  />
                </div>

                <div className="survey-form-group">
                  <label htmlFor="edit-valid-till">
                    <MdCalendarToday className="survey-label-icon" />
                    Valid Till (IST) <span className="required">*</span>
                  </label>
                  {/* Wrapper keeps react-datepicker's injected tab-loop div out of the form
                      group's flex gap - without it, opening the calendar adds a gap and shifts
                      the fields below down. */}
                  <div className="survey-datetime-field">
                    <DatePicker
                      id="edit-valid-till"
                      selected={parseDateTimeLocal(editValidTillDateTime)}
                      onChange={(date) => setEditValidTillDateTime(date ? toDateTimeLocalValue(date) : '')}
                      showTimeSelect
                      timeIntervals={15}
                      timeCaption="Time"
                      dateFormat="MMM d, yyyy  h:mm aa"
                      minDate={new Date()}
                      filterTime={isFutureTime}
                      placeholderText="Select date and time"
                      className="survey-datetime-input"
                      calendarClassName="survey-datepicker"
                      popperPlacement="bottom-start"
                      /* Fixed strategy escapes the modal overflow that was clipping the
                         calendar, and lets floating-ui flip it above the field near the
                         viewport bottom instead of running off the page. */
                      popperProps={{ strategy: 'fixed' }}
                      autoComplete="off"
                      open={isValidTillOpen}
                      onInputClick={() => setIsValidTillOpen(true)}
                      onFocus={() => setIsValidTillOpen(true)}
                      onClickOutside={() => setIsValidTillOpen(false)}
                      shouldCloseOnSelect={false}
                    >
                      <div className="survey-datepicker-footer">
                        <button
                          type="button"
                          className="survey-datepicker-btn"
                          onClick={() => {
                            setEditValidTillDateTime('');
                            setIsValidTillOpen(false);
                          }}
                        >
                          Clear
                        </button>
                        <button
                          type="button"
                          className="survey-datepicker-btn survey-datepicker-btn--primary"
                          onClick={() => setIsValidTillOpen(false)}
                        >
                          OK
                        </button>
                      </div>
                    </DatePicker>
                  </div>
                </div>

                <div className="survey-form-group">
                  <label htmlFor="edit-survey-location">Location <span className="required">*</span></label>
                  <MultiSelectCheckboxDropdown
                    id="edit-survey-location"
                    options={locations}
                    selected={editSelectedLocations}
                    onChange={setEditSelectedLocations}
                    placeholder="-- Select Location --"
                    allLabel="All Locations"
                  />
                </div>

                <div className="survey-form-group">
                  <label>Show Survey To <span className="required">*</span></label>
                  <div className="survey-role-grid survey-target-type-grid">
                    {TARGET_TYPE_OPTIONS.map((option) => (
                      <label key={option.value} className="survey-role-item">
                        <input
                          type="radio"
                          name="clone-survey-target-type"
                          checked={cloneTargetType === option.value}
                          onChange={() => handleCloneTargetTypeChange(option.value)}
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>

                  <label>
                    {cloneTargetType === 'DEPARTMENT' ? 'Departments' : 'Roles'} <span className="required">*</span>
                  </label>
                  <div className="survey-role-grid">
                    {(cloneTargetType === 'DEPARTMENT' ? departments : ROLE_OPTIONS).map((value) => (
                      <label key={value} className="survey-role-item">
                        <input
                          type="checkbox"
                          checked={cloneTargetValues.includes(value)}
                          onChange={() => handleCloneTargetValueToggle(value)}
                        />
                        <span>{cloneTargetType === 'DEPARTMENT' ? value : formatRoleLabel(value)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="survey-form-group">
                  <div className="survey-question-header">
                    <label>Questions <span className="required">*</span></label>
                  </div>

                  <div className="survey-question-list">
                    {editQuestions.map((question, index) => {
                      const isDuplicateOrder = editDuplicateDisplayOrders.has(Number(question.displayOrder));
                      return (
                      <div className="survey-question-card" key={`edit-question-${index}`}>
                        <div className="survey-question-top">
                          <strong>Question {index + 1}</strong>
                          <button
                            type="button"
                            className="survey-remove-btn"
                            onClick={() => handleRemoveEditQuestion(index)}
                            disabled={editQuestions.length === 1}
                          >
                            Remove
                          </button>
                        </div>

                        <div className="survey-question-grid">
                          <div className="survey-form-group compact">
                            <label>Question <span className="required">*</span></label>
                            <input
                              type="text"
                              value={question.question}
                              onChange={(e) => handleEditQuestionChange(index, 'question', e.target.value)}
                              placeholder="How satisfied are you with your work?"
                            />
                          </div>

                          <div className="survey-form-group compact">
                            <label>Type</label>
                            <select
                              value={question.type}
                              onChange={(e) => handleEditQuestionChange(index, 'type', e.target.value)}
                            >
                              {QUESTION_TYPES.map((type) => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                          </div>

                          <div className="survey-form-group compact">
                            <label>Display Order <span className="required">*</span></label>
                            <input
                              type="number"
                              min="1"
                              step="1"
                              className={isDuplicateOrder ? 'survey-input-error' : undefined}
                              value={question.displayOrder}
                              onChange={(e) => handleEditQuestionChange(index, 'displayOrder', e.target.value)}
                              placeholder="1"
                            />
                            {isDuplicateOrder && (
                              <small className="survey-duplicate-error">Display Order already exists. Please select a unique display order.</small>
                            )}
                          </div>
                        </div>

                        <label className="survey-required-check">
                          <input
                            type="checkbox"
                            checked={question.required}
                            onChange={(e) => handleEditQuestionChange(index, 'required', e.target.checked)}
                          />
                          <span>Required</span>
                        </label>

                        {OPTION_BASED_TYPES.has(question.type) && (
                          <div className="survey-form-group compact">
                            <label>Options <span className="required">*</span></label>
                            <textarea
                              rows={4}
                              value={question.optionsText}
                              onChange={(e) => handleEditQuestionChange(index, 'optionsText', e.target.value)}
                              placeholder={'Option 1\nOption 2\nOption 3'}
                            />
                            <small className="survey-info-line">Enter one option per line.</small>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>

                <div className="survey-submit-row survey-submit-row-split">
                  <button type="button" className="btn-secondary btn-add-green" onClick={handleAddEditQuestion}>
                    <MdAdd />
                    <span>Add Question</span>
                  </button>
                  <div className="survey-submit-actions-right">
                    <button type="button" className="btn-secondary survey-cancel-btn" onClick={() => closeEditModal()} disabled={isSavingEdit}>
                      Cancel
                    </button>
                    <button className="btn-primary" type="button" onClick={handleSaveSurveyForm} disabled={isSavingEdit}>
                      <MdAddCircleOutline />
                      <span>
                        {isSavingEdit
                          ? (surveyFormMode === 'clone' ? 'Creating...' : 'Saving...')
                          : (surveyFormMode === 'clone' ? 'Create Survey' : 'Save Changes')}
                      </span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={showCloseConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to cancel?"
        confirmLabel="Discard Changes"
        cancelLabel="Keep Editing"
        danger
        onConfirm={performCloseEditModal}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </div>
  );
}

export default SurveyAnalytics;
